import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TabViewModule } from 'primeng/tabview';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { AccordionModule } from 'primeng/accordion';
import { CardModule } from 'primeng/card';
import { ChipModule } from 'primeng/chip';
import { TooltipModule } from 'primeng/tooltip';
import { DividerModule } from 'primeng/divider';

export interface Tutorial {
  id: string;
  title: string;
  description: string;
  steps: string[];
  category: string;
  icon: string;
  difficulty: 'Fácil' | 'Médio' | 'Avançado';
  estimatedTime: string;
}

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  tags: string[];
}

@Component({
  selector: 'app-tutorial-faq',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule,
    TabViewModule, 
    InputTextModule, 
    ButtonModule, 
    AccordionModule, 
    CardModule, 
    ChipModule, 
    TooltipModule, 
    DividerModule
  ],
  template: `
    <div class="tutorial-faq-container" *ngIf="!minimizado">
      <div class="header">
        <h4>
          <i class="pi pi-question-circle"></i>
          Tutoriais & FAQ
        </h4>
        <div class="search-container">
          <span class="p-input-icon-left">
            <i class="pi pi-search"></i>
            <input 
              pInputText 
              type="text" 
              placeholder="Buscar tutoriais ou perguntas..." 
              [(ngModel)]="searchTerm"
              (input)="filterContent()"
            />
          </span>
        </div>
        <button pButton type="button" icon="pi pi-minus" class="p-button-text" (click)="minimizar()" pTooltip="Minimizar" appendTo="body"></button>
      </div>

      <p-tabView>
        <!-- Aba Tutoriais -->
        <p-tabPanel header="Tutoriais" leftIcon="pi pi-play">
          <div class="tutorials-grid">
            <div *ngFor="let tutorial of filteredTutorials" class="tutorial-card">
              <div class="tutorial-header">
                <i [ngClass]="tutorial.icon"></i>
                <div class="tutorial-info">
                  <h5>{{ tutorial.title }}</h5>
                  <p>{{ tutorial.description }}</p>
                </div>
                <div class="tutorial-meta">
                  <p-chip [label]="tutorial.difficulty" [styleClass]="getDifficultyClass(tutorial.difficulty)"></p-chip>
                  <span class="time-estimate">
                    <i class="pi pi-clock"></i>
                    {{ tutorial.estimatedTime }}
                  </span>
                </div>
              </div>
              
              <div class="tutorial-steps">
                <h6>Passos:</h6>
                <ol>
                  <li *ngFor="let step of tutorial.steps">{{ step }}</li>
                </ol>
              </div>
              
              <div class="tutorial-footer">
                <p-chip [label]="tutorial.category" icon="pi pi-tag" styleClass="category-chip"></p-chip>
              </div>
            </div>
          </div>
        </p-tabPanel>

        <!-- Aba FAQ -->
        <p-tabPanel header="Perguntas Frequentes" leftIcon="pi pi-question">
          <div class="faq-container">
            <div class="faq-categories">
              <button 
                *ngFor="let category of faqCategories" 
                pButton 
                type="button" 
                [label]="category"
                [class]="selectedFaqCategory === category ? 'p-button-primary' : 'p-button-outlined'"
                (click)="selectFaqCategory(category)"
                class="category-btn"
              ></button>
            </div>

            <p-accordion styleClass="faq-accordion">
              <p-accordionTab 
                *ngFor="let faq of filteredFaqs" 
                [header]="faq.question"
              >
                <div class="faq-answer">
                  <p>{{ faq.answer }}</p>
                  <div class="faq-tags">
                    <p-chip *ngFor="let tag of faq.tags" [label]="tag" styleClass="tag-chip"></p-chip>
                  </div>
                </div>
              </p-accordionTab>
            </p-accordion>
          </div>
        </p-tabPanel>

        <!-- Aba Dicas Rápidas -->
        <p-tabPanel header="Dicas Rápidas" leftIcon="pi pi-lightbulb">
          <div class="quick-tips">
            <div class="tips-grid">
              <div *ngFor="let tip of quickTips" class="tip-card">
                <div class="tip-icon">
                  <i [ngClass]="tip.icon"></i>
                </div>
                <div class="tip-content">
                  <h6>{{ tip.title }}</h6>
                  <p>{{ tip.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </p-tabPanel>
      </p-tabView>
    </div>

    <div *ngIf="minimizado" class="minimized-container">
      <button pButton type="button" icon="pi pi-plus" label="Mostrar Tutoriais & FAQ" (click)="minimizado = false" class="p-button-outlined"></button>
    </div>
  `,
  styleUrls: ['./tutorial-faq.component.scss']
})
export class TutorialFaqComponent {
  minimizado = false;
  searchTerm = '';
  selectedFaqCategory = 'Todos';
  
  tutorials: Tutorial[] = [
    {
      id: 'cadastrar-cliente',
      title: 'Cadastrar Novo Cliente',
      description: 'Aprenda a cadastrar clientes no sistema com todos os dados necessários',
      steps: [
        'Acesse "Clientes" no menu principal',
        'Clique em "Cadastrar Cliente"',
        'Preencha os dados pessoais (nome, CPF/CNPJ, telefone, email)',
        'Adicione o endereço completo',
        'Clique em "Salvar" para finalizar o cadastro'
      ],
      category: 'Clientes',
      icon: 'pi pi-user-plus',
      difficulty: 'Fácil',
      estimatedTime: '5 min'
    },
    {
      id: 'editar-cliente',
      title: 'Editar Dados do Cliente',
      description: 'Como atualizar informações de clientes já cadastrados',
      steps: [
        'Acesse "Clientes" > "Listagem"',
        'Localize o cliente na tabela',
        'Clique no ícone de editar (lápis)',
        'Modifique os dados necessários',
        'Clique em "Salvar" para confirmar as alterações'
      ],
      category: 'Clientes',
      icon: 'pi pi-user-edit',
      difficulty: 'Fácil',
      estimatedTime: '3 min'
    },
    {
      id: 'visualizar-cliente',
      title: 'Visualizar Detalhes do Cliente',
      description: 'Como acessar todas as informações de um cliente',
      steps: [
        'Acesse "Clientes" > "Listagem"',
        'Localize o cliente na tabela',
        'Clique no ícone de visualizar (olho)',
        'Visualize todos os dados do cliente no modal',
        'Feche o modal clicando em "Fechar"'
      ],
      category: 'Clientes',
      icon: 'pi pi-eye',
      difficulty: 'Fácil',
      estimatedTime: '2 min'
    },

    {
      id: 'cadastrar-socio',
      title: 'Cadastrar Novo Sócio',
      description: 'Cadastre sócios da empresa com dados completos para eSocial',
      steps: [
        'Acesse "Sócios" no menu principal',
        'Clique em "Adicionar Sócio"',
        'Preencha dados pessoais (nome, CPF, data nascimento, etc.)',
        'Adicione informações profissionais e fiscais',
        'Configure dados de dependentes se necessário',
        'Preencha o valor do Pró-Labore mensal',
        'Clique em "Salvar" para finalizar'
      ],
      category: 'Sócios',
      icon: 'pi pi-users',
      difficulty: 'Médio',
      estimatedTime: '8 min'
    },
    {
      id: 'calcular-pro-labore',
      title: '“Calcular tributos sobre pró-labore',
      description: 'Use o cálculo automático do INSS e IRRF',
      steps: [
        'No cadastro de sócio, preencha o "Valor do Pró-Labore Mensal"',
        'Saia do campo (clique fora) para calcular automaticamente',
        'O sistema calculará INSS, IRRF e valor líquido',
        'Os campos calculados ficarão bloqueados para edição',
        'Use o botão "Recalcular" se precisar ajustar valores'
      ],
      category: 'Sócios',
      icon: 'pi pi-calculator',
      difficulty: 'Fácil',
      estimatedTime: '3 min'
    },

    {
      id: 'configurar-plano-contas',
      title: 'Configurar Plano de Contas',
      description: 'Configure o plano de contas contábil da empresa',
      steps: [
        'Acesse "Plano de Contas" no menu',
        'Visualize as contas organizadas por natureza',
        'Clique em "Nova Conta" para adicionar contas',
        'Selecione a conta pai e preencha os dados',
        'Configure saldo inicial se necessário',
        'Salve a nova conta no plano'
      ],
      category: 'Contabilidade',
      icon: 'pi pi-book',
      difficulty: 'Médio',
      estimatedTime: '10 min'
    },
    {
      id: 'editar-saldo-conta',
      title: 'Editar Saldo Inicial de conta',
      description: 'Como ajustar saldos iniciais das contas contábeis',
      steps: [
        'Acesse "Plano de Contas"',
        'Localize a conta que deseja editar',
        'Clique no valor do saldo para editá-lo',
        'Digite o novo valor',
        'Pressione Enter ou clique fora para salvar',
        'Confirme a alteração'
      ],
      category: 'Contabilidade',
      icon: 'pi pi-dollar',
      difficulty: 'Fácil',
      estimatedTime: '2 min'
    },

    {
      id: 'criar-descricao-padrao',
      title: 'Criar Descrição Padrão',
      description: 'Crie descrições padrão para agilizar a emissão de notas fiscais',
      steps: [
        'Acesse "Descrições Padrão" no menu',
        'Clique em "Adicionar Descrição"',
        'Digite a descrição que será usada frequentemente',
        'Clique em "Salvar" para criar a descrição',
        'Use a descrição nas emissões de NFSe futuras.'
      ],
      category: 'Contabilidade',
      icon: 'pi pi-file-edit',
      difficulty: 'Fácil',
      estimatedTime: '2 min'
    },

    {
      id: 'import-extrato',
      title: 'Importar Extrato Bancário',
      description: 'Aprenda a importar e classificar extratos bancários no sistema',
      steps: [
        'Acesse "Extratos" no menu principal',
        'Clique em "Importar Extrato"',
        'Escolha entre arquivo OFX ou planilha Excel',
        'Selecione o arquivo do seu computador',
        'Confirme os dados e clique em "Importar"',
        'Classifique as transações conforme necessário'
      ],
      category: 'Extratos',
      icon: 'pi pi-download',
      difficulty: 'Fácil',
      estimatedTime: '5 min'
    },
    {
      id: 'classificar-transacoes',
      title: 'Classificar Transações do Extrato',
      description: 'Como classificar transações bancárias automaticamente',
      steps: [
        'Após importar o extrato, acesse a lista de transações',
        'Use o filtro para encontrar transações não classificadas',
        'Clique em "Classificar" na transação',
        'Selecione a conta contábil correspondente',
        'Adicione uma descrição se necessário',
        'Confirme a classificação'
      ],
      category: 'Extratos',
      icon: 'pi pi-tags',
      difficulty: 'Médio',
      estimatedTime: '8 min'
    },

    {
      id: 'cadastrar-conta-bancaria',
      title: 'Cadastrar Conta Bancária',
      description: 'Configure contas bancárias para importação de extratos',
      steps: [
        'Acesse "Contas Bancárias" no menu',
        'Clique em "Nova Conta"',
        'Preencha dados do banco e agência',
        'Informe o número da conta',
        'Salve a conta bancária'
      ],
      category: 'Bancos',
      icon: 'pi pi-credit-card',
      difficulty: 'Fácil',
      estimatedTime: '3 min'
    },

    {
      id: 'fazer-lancamento-caixa',
      title: 'Fazer Lançamento de Caixa',
      description: 'Registre movimentações de caixa manualmente',
      steps: [
        'Acesse "Lançamentos de Caixa"',
        'Clique em "Novo Lançamento"',
        'Selecione o tipo (receita/despesa)',
        'Escolha a conta contábil',
        'Digite o valor e a descrição',
        'Informe a data do lançamento',
        'Salve o lançamento'
      ],
      category: 'Caixa',
      icon: 'pi pi-money-bill',
      difficulty: 'Fácil',
      estimatedTime: '5 min'
    },

    {
      id: 'emitir-nfse',
      title: 'Emitir NFS-e',
      description: 'Como emitir notas fiscais de serviço eletrônicas',
      steps: [
        'Acesse "NFS-e" no menu principal',
        'Clique em "Nova NFS-e"',
        'Selecione o cliente ou cadastre um novo',
        'Preencha os dados do serviço prestado',
        'Revise os dados e emita a nota fiscal'
      ],
      category: 'NFS-e',
      icon: 'pi pi-file-export',
      difficulty: 'Médio',
      estimatedTime: '10 min'
    },
    {
      id: 'importar-nfse',
      title: 'Importar NFS-e',
      description: 'Importe NFS-e emitidas em outros sistemas',
      steps: [
        'Acesse "NFS-e" > "Importar"',
        'Baixe o modelo de planilha',
        'Preencha com os dados das NFS-e emitidas fora do advventure e que estejam válidas na Prefeitura',
        'Faça upload da planilha',
        'Confirme os dados importados',
        'Salve as NFS-e no sistema'
      ],
      category: 'NFS-e',
      icon: 'pi pi-upload',
      difficulty: 'Médio',
      estimatedTime: '8 min'
    },
    {
      id: 'cancelar-nfse',
      title: 'Cancelar NFS-e',
      description: 'Como cancelar uma NFS-e já emitida',
      steps: [
        'Acesse "NFS-e" > "Listagem"',
        'Localize a NFS-e que deseja cancelar',
        'Verifique se o status permite cancelamento',
        'Clique em "Cancelar"',
        'Informe a justificativa do cancelamento',
        'Confirme o cancelamento'
      ],
      category: 'NFS-e',
      icon: 'pi pi-times-circle',
      difficulty: 'Fácil',
      estimatedTime: '3 min'
    },

    {
      id: 'visualizar-declaracoes',
      title: 'Visualizar e Baixar Declarações',
      description: 'Acesse e baixe declarações fiscais já entregues',
      steps: [
        'Acesse "Declarações Fiscais" no menu',
        'Visualize as declarações mensais e anuais',
        'Localize a declaração desejada na lista',
        'Clique em "Declaração" para baixar o documento',
        'Clique em "Recibo" para baixar o protocolo',
        'Os arquivos serão salvos automaticamente'
      ],
      category: 'Declarações',
      icon: 'pi pi-download',
      difficulty: 'Fácil',
      estimatedTime: '3 min'
    },

    {
      id: 'gerar-balancete',
      title: 'Gerar Balancete',
      description: 'Como gerar relatório balancete contábil',
      steps: [
        'Acesse "Relatórios" > "Balancete"',
        'Selecione o período desejado',
        'Clique em "Gerar Relatório"',
        'Visualize e baixe o Arquivo'
      ],
      category: 'Relatórios',
      icon: 'pi pi-file-pdf',
      difficulty: 'Médio',
      estimatedTime: '5 min'
    },
    {
      id: 'gerar-razao',
      title: 'Gerar Razão Geral',
      description: 'Gere relatório razão geral das contas',
      steps: [
        'Acesse "Relatórios" > "Razão"',
        'Selecione o período desejado',
        'Clique em "Gerar Relatório"',
        'Visualize e baixe o Arquivo'
      ],
      category: 'Relatórios',
      icon: 'pi pi-chart-bar',
      difficulty: 'Médio',
      estimatedTime: '5 min'
    },
    {
      id: 'gerar-dre',
      title: 'Gerar DRE (Demonstração do Resultado)',
      description: 'Como gerar relatório DRE da empresa',
      steps: [
        'Acesse "Relatórios" > "DRE"',
        'Selecione o período desejado',
        'Clique em "Gerar Relatório"',
        'Visualize e baixe o Arquivo'
      ],
      category: 'Relatórios',
      icon: 'pi pi-chart-line',
      difficulty: 'Avançado',
      estimatedTime: '8 min'
    },
    {
      id: 'gerar-balanco',
      title: 'Gerar Balanço Patrimonial',
      description: 'Gere balanço patrimonial da empresa',
      steps: [
        'Acesse "Relatórios" > "Balanço Patrimonial"',
        'Selecione o período desejado',
        'Clique em "Gerar Relatório"',
        'Visualize e baixe o Arquivo'
      ],
      category: 'Relatórios',
      icon: 'pi pi-chart-pie',
      difficulty: 'Avançado',
      estimatedTime: '8 min'
    },

    {
      id: 'trocar-certificado',
      title: 'Trocar Certificado Digital',
      description: 'Aprenda a atualizar o certificado digital A1 da empresa',
      steps: [
        'Acesse "Configurações" > "Dados da Empresa"',
        'Role a página até a seção "Certificado Digital"',
        'Clique em "Selecionar Certificado" e escolha o arquivo .pfx (máx. 5MB)',
        'Digite a senha definida na emissão do certificado',
        'Clique em "Atualizar Certificado" para concluir',
        'Atenção: o certificado só será aceito se estiver válido, for do tipo A1 e estiver vinculado ao CNPJ desta empresa'
      ],
      category: 'Empresa',
      icon: 'pi pi-shield',
      difficulty: 'Fácil',
      estimatedTime: '2 min'
    }
  ];

  faqs: FAQ[] = [
    {
      id: 'faq-clientes-1',
      question: 'Como cadastrar um novo cliente no sistema?',
      answer: 'Acesse "Clientes" no menu principal, clique em "Cadastrar Cliente" e preencha todos os dados obrigatórios (nome, CPF/CNPJ, telefone, email e endereço). Clique em "Salvar" para finalizar o cadastro.',
      category: 'Clientes',
      tags: ['cadastro', 'cliente', 'dados', 'obrigatórios']
    },
    {
      id: 'faq-clientes-2',
      question: 'Posso editar dados de um cliente já cadastrado?',
      answer: 'Sim! Acesse "Clientes" > "Listagem", localize o cliente na tabela e clique no ícone de editar (lápis). Modifique os dados necessários e clique em "Salvar".',
      category: 'Clientes',
      tags: ['editar', 'atualizar', 'dados', 'modificar']
    },
    {
      id: 'faq-clientes-3',
      question: 'Como visualizar todos os dados de um cliente?',
      answer: 'Na listagem de clientes, clique no ícone de visualizar (olho) ao lado do cliente desejado. Um modal será aberto mostrando todos os dados cadastrais do cliente.',
      category: 'Clientes',
      tags: ['visualizar', 'detalhes', 'modal', 'informações']
    },

    {
      id: 'faq-socios-1',
      question: 'Como cadastrar um sócio da empresa?',
      answer: 'Acesse "Sócios" no menu, clique em "Adicionar Sócio" e preencha todos os dados pessoais, profissionais e fiscais. Não esqueça de informar o valor do Pró-Labore mensal.',
      category: 'Sócios',
      tags: ['cadastro', 'sócio', 'pró-labore', 'dados']
    },
    {
      id: 'faq-socios-2',
      question: 'Como funciona o cálculo automático de Pró-Labore?',
      answer: 'Após preencher o valor do Pró-Labore mensal, saia do campo (clique fora) e o sistema calculará automaticamente INSS, IRRF e valor líquido. Os campos calculados ficarão bloqueados para edição.',
      category: 'Sócios',
      tags: ['cálculo', 'pró-labore', 'INSS', 'IRRF', 'automático']
    },
    {
      id: 'faq-socios-3',
      question: 'Posso recalcular o Pró-Labore depois?',
      answer: 'Sim! Use o botão "Recalcular" ao lado do campo de valor para refazer o cálculo. Isso é útil se você alterar o valor ou o número de dependentes.',
      category: 'Sócios',
      tags: ['recalcular', 'ajustar', 'dependentes', 'valor']
    },

    {
      id: 'faq-contas-1',
      question: 'Como criar uma nova conta no plano de contas?',
      answer: 'Acesse "Plano de Contas", clique em "Nova Conta", selecione a conta pai, preencha a descrição e configure o saldo inicial se necessário. Clique em "Salvar" para criar a conta.',
      category: 'Contabilidade',
      tags: ['nova conta', 'plano', 'descrição', 'saldo']
    },
    {
      id: 'faq-contas-2',
      question: 'Como editar o saldo de uma conta?',
      answer: 'No plano de contas, clique diretamente no valor do saldo que deseja editar. Digite o novo valor e pressione Enter ou clique fora para salvar a alteração.',
      category: 'Contabilidade',
      tags: ['editar saldo', 'valor', 'ajustar', 'modificar']
    },
    {
      id: 'faq-contas-3',
      question: 'O que são descrições padrão?',
      answer: 'Descrições padrão são textos pré-definidos que você pode usar frequentemente nos lançamentos. Isso agiliza o processo e evita erros de digitação.',
      category: 'Contabilidade',
      tags: ['descrições', 'padrão', 'agilizar', 'lançamentos']
    },

    {
      id: 'faq-extratos-1',
      question: 'Como importar extratos de diferentes bancos?',
      answer: 'O sistema aceita arquivos OFX (padrão bancário) e planilhas Excel. Para OFX, basta selecionar o arquivo diretamente. Para planilhas, use o modelo disponível para download no sistema.',
      category: 'Extratos',
      tags: ['importação', 'extrato', 'OFX', 'planilha', 'bancos']
    },
    {
      id: 'faq-extratos-2',
      question: 'Como classificar transações do extrato?',
      answer: 'Após importar o extrato, acesse a lista de transações, use filtros para encontrar transações não classificadas, clique em "Classificar" e selecione a conta contábil correspondente.',
      category: 'Extratos',
      tags: ['classificar', 'transações', 'conta', 'contábil']
    },
    {
      id: 'faq-extratos-3',
      question: 'Preciso cadastrar contas bancárias?',
      answer: 'Sim! Cadastre suas contas bancárias em "Contas Bancárias" para facilitar a importação e classificação de extratos. Isso ajuda o sistema a identificar automaticamente as transações.',
      category: 'Extratos',
      tags: ['contas bancárias', 'cadastro', 'importação', 'identificação']
    },

    {
      id: 'faq-lancamentos-1',
      question: 'Como fazer lançamentos de caixa manualmente?',
      answer: 'Acesse "Lançamentos de Caixa", clique em "Novo Lançamento", selecione o tipo (receita/despesa), escolha a conta contábil, digite o valor e descrição, e salve o lançamento.',
      category: 'Caixa',
      tags: ['lançamento', 'manual', 'receita', 'despesa', 'caixa']
    },

    {
      id: 'faq-nfse-1',
      question: 'Como emitir uma NFS-e?',
      answer: 'Acesse "NFS-e" no menu, clique em "Nova NFS-e", selecione o cliente, preencha os dados do serviço, configure as alíquotas de impostos e emita a nota fiscal.',
      category: 'NFS-e',
      tags: ['emitir', 'NFS-e', 'cliente', 'serviço', 'impostos']
    },
    {
      id: 'faq-nfse-2',
      question: 'Posso cancelar uma NFS-e já emitida?',
      answer: 'Sim, você pode cancelar NFS-e com status "Autorizada". Acesse a lista de NFS-e, localize a nota e clique em "Cancelar". É necessário informar uma justificativa para o cancelamento.',
      category: 'NFS-e',
      tags: ['cancelamento', 'NFS-e', 'justificativa', 'autorizada']
    },
    {
      id: 'faq-nfse-3',
      question: 'Qual a diferença entre emitir e importar NFS-e?',
      answer: 'Emitir NFS-e é criar uma nova nota fiscal diretamente no sistema. Importar NFS-e é trazer para o sistema notas que foram emitidas em outros sistemas ou plataformas.',
      category: 'NFS-e',
      tags: ['emissão', 'importação', 'diferença', 'sistemas']
    },

    {
      id: 'faq-declaracoes-1',
      question: 'Como visualizar e baixar declarações já enviadas?',
      answer: 'Acesse "Declarações Fiscais" no menu, visualize as declarações mensais e anuais, localize a desejada e clique em "Declaração" ou "Recibo" para baixar os documentos.',
      category: 'Declarações',
      tags: ['visualizar', 'baixar', 'recibo', 'protocolo', 'histórico']
    },
    {
      id: 'faq-declaracoes-2',
      question: 'O que fazer se uma declaração for rejeitada?',
      answer: 'Verifique os erros indicados na rejeição, corrija os dados no sistema e reenvie a declaração. O sistema mantém histórico de todas as tentativas de envio.',
      category: 'Declarações',
      tags: ['rejeição', 'erro', 'reenvio', 'correção', 'histórico']
    },

    {
      id: 'faq-folha-1',
      question: 'Como gerar folha de pagamento?',
      answer: 'Acesse "Folha de Pagamentos", clique em "Nova Folha", selecione o período, adicione os funcionários, configure salários e benefícios, e calcule automaticamente os impostos.',
      category: 'Folha',
      tags: ['folha', 'pagamento', 'funcionários', 'salários', 'impostos']
    },

    {
      id: 'faq-relatorios-1',
      question: 'Como gerar relatórios contábeis?',
      answer: 'Acesse "Relatórios" no menu e escolha o tipo desejado: Balancete, Razão, DRE ou Balanço Patrimonial. Configure o período e opções, depois clique em "Gerar Relatório".',
      category: 'Relatórios',
      tags: ['relatórios', 'contábeis', 'balancete', 'razão', 'DRE', 'balanço']
    },
    {
      id: 'faq-relatorios-2',
      question: 'Em que formato são gerados os relatórios?',
      answer: 'Todos os relatórios são gerados em formato Excel, que pode ser visualizado no navegador, baixado para o computador ou impresso.',
      category: 'Relatórios',
      tags: ['Excel', 'formato', 'visualizar', 'baixar', 'imprimir']
    },

    {
      id: 'faq-tributos-1',
      question: 'Como funciona o cálculo automático de impostos?',
      answer: 'O sistema calcula automaticamente INSS, IRRF e outros impostos baseado nas alíquotas configuradas e no regime tributário da empresa. Os valores são atualizados conforme a legislação vigente.',
      category: 'Tributos',
      tags: ['cálculo', 'impostos', 'alíquotas', 'automático', 'legislação']
    },
    {
      id: 'faq-tributos-2',
      question: 'Como configurar tributos no sistema?',
      answer: 'Acesse "Tributos" no menu, selecione o tipo de tributo, configure as alíquotas vigentes, defina as bases de cálculo e salve as configurações.',
      category: 'Tributos',
      tags: ['configurar', 'tributos', 'alíquotas', 'bases', 'cálculo']
    },

    {
      id: 'faq-empresa-1',
      question: 'Como configurar dados da empresa?',
      answer: 'Acesse "Empresa" no menu, preencha dados cadastrais completos, configure endereço e contatos, adicione dados fiscais e configure o regime tributário.',
      category: 'Empresa',
      tags: ['configurar', 'empresa', 'dados', 'fiscais', 'regime']
    },
    {
      id: 'faq-empresa-2',
      question: 'Por que meu certificado digital não foi aceito?',
      answer: 'O certificado só será aceito se estiver válido, for do tipo A1 e estiver vinculado ao CNPJ desta empresa. Verifique esses três pontos antes de tentar novamente.',
      category: 'Empresa',
      tags: ['certificado', 'A1', 'CNPJ', 'validade']
    },
    {
      id: 'faq-empresa-3',
      question: 'Qual tipo de certificado digital o sistema aceita?',
      answer: 'Apenas certificados digitais do tipo A1, no formato .pfx, com tamanho máximo de 5MB.',
      category: 'Empresa',
      tags: ['certificado', 'A1', 'pfx']
    },

    {
      id: 'faq-saldo-1',
      question: 'Como configurar saldos iniciais das contas?',
      answer: 'Acesse "Saldo Inicial" no menu, selecione as contas que precisam de saldo inicial, digite os valores de abertura, confirme se são débitos ou créditos e salve.',
      category: 'Contabilidade',
      tags: ['saldo inicial', 'contas', 'valores', 'débito', 'crédito']
    },

    {
      id: 'faq-acesso-1',
      question: 'O que fazer se esquecer minha senha?',
      answer: 'Na tela de login, clique em "Esqueci minha senha" e informe seu email cadastrado. Você receberá um link para redefinir sua senha de forma segura.',
      category: 'Acesso',
      tags: ['senha', 'recuperação', 'email', 'segurança', 'login']
    },
    {
      id: 'faq-acesso-2',
      question: 'Como manter meus dados seguros?',
      answer: 'Use senhas fortes, não compartilhe suas credenciais, faça logout ao sair do sistema e mantenha seu navegador atualizado. O sistema faz backup automático dos dados.',
      category: 'Acesso',
      tags: ['segurança', 'senha', 'credenciais', 'backup', 'logout']
    },
    {
      id: 'faq-acesso-3',
      question: 'O sistema faz backup dos meus dados?',
      answer: 'Sim! O sistema faz backup automático dos seus dados a cada 24 horas. Todos os dados são armazenados de forma segura e podem ser recuperados se necessário.',
      category: 'Acesso',
      tags: ['backup', 'automático', 'dados', 'segurança', 'recuperação']
    }
  ];

  quickTips = [
    {
      title: 'Atalhos de Teclado',
      description: 'Use Ctrl+S para salvar, Ctrl+F para buscar, Ctrl+P para imprimir relatórios e Tab para navegar entre campos',
      icon: 'pi pi-keyboard'
    },
    {
      title: 'Busca Inteligente',
      description: 'Use a barra de pesquisa em qualquer listagem para encontrar rapidamente o que procura',
      icon: 'pi pi-search'
    },
    {
      title: 'Validação em Tempo Real',
      description: 'Os formulários validam dados em tempo real, evitando erros de preenchimento',
      icon: 'pi pi-check-circle'
    },

    {
      title: 'Backup Automático',
      description: 'O sistema faz backup automático dos seus dados a cada 24 horas',
      icon: 'pi pi-cloud'
    },
    {
      title: 'Histórico Completo',
      description: 'Todas as operações ficam registradas no histórico para auditoria',
      icon: 'pi pi-history'
    },
    {
      title: 'Logout Seguro',
      description: 'Sempre faça logout ao sair do sistema para proteger seus dados',
      icon: 'pi pi-sign-out'
    },

    {
      title: 'Atualizações Automáticas',
      description: 'O sistema é atualizado automaticamente com as últimas mudanças da legislação',
      icon: 'pi pi-sync'
    },
    {
      title: 'Cálculo Automático',
      description: 'O sistema calcula automaticamente impostos, pró-labore e outros valores',
      icon: 'pi pi-calculator'
    },
    {
      title: 'Importação de Dados',
      description: 'Importe extratos OFX, planilhas Excel e NFS-e de outros sistemas',
      icon: 'pi pi-download'
    },
    {
      title: 'Relatórios em Arquivo',
      description: 'Todos os relatórios são gerados em .xlsx para fácil visualização e impressão',
      icon: 'pi pi-file-excel'
    },
    {
      title: 'Filtros Avançados',
      description: 'Use filtros nas listagens para encontrar rapidamente os dados que precisa',
      icon: 'pi pi-filter'
    },
    {
      title: 'Tooltips Informativos',
      description: 'Passe o mouse sobre ícones e campos para ver dicas e explicações',
      icon: 'pi pi-info-circle'
    }
  ];

  filteredTutorials: Tutorial[] = this.tutorials;
  filteredFaqs: FAQ[] = this.faqs;
  faqCategories = ['Todos', 'Clientes', 'Sócios', 'Contabilidade', 'Extratos', 'Caixa', 'NFS-e', 'Declarações', 'Folha', 'Relatórios', 'Tributos', 'Empresa', 'Acesso'];

  minimizar() { 
    this.minimizado = true; 
  }

  filterContent() {
    if (!this.searchTerm.trim()) {
      this.filteredTutorials = this.tutorials;
      this.filteredFaqs = this.faqs;
      return;
    }

    const term = this.searchTerm.toLowerCase();
    
    this.filteredTutorials = this.tutorials.filter(tutorial =>
      tutorial.title.toLowerCase().includes(term) ||
      tutorial.description.toLowerCase().includes(term) ||
      tutorial.category.toLowerCase().includes(term) ||
      tutorial.steps.some(step => step.toLowerCase().includes(term))
    );

    this.filteredFaqs = this.faqs.filter(faq =>
      faq.question.toLowerCase().includes(term) ||
      faq.answer.toLowerCase().includes(term) ||
      faq.category.toLowerCase().includes(term) ||
      faq.tags.some(tag => tag.toLowerCase().includes(term))
    );
  }

  selectFaqCategory(category: string) {
    this.selectedFaqCategory = category;
    if (category === 'Todos') {
      this.filteredFaqs = this.faqs;
    } else {
      this.filteredFaqs = this.faqs.filter(faq => faq.category === category);
    }
  }

  getDifficultyClass(difficulty: string): string {
    switch (difficulty) {
      case 'Fácil': return 'difficulty-easy';
      case 'Médio': return 'difficulty-medium';
      case 'Avançado': return 'difficulty-hard';
      default: return '';
    }
  }
} 
